import { Stack, Typography, useTheme } from '@mui/material';
import { Circle } from 'lucide-react';

import {
  baseChipSx,
  chipTypographyStyles,
} from '@modules/_shared/components/feed-v2/components/statusChipStyles';
import type { StageSnapshot } from '@modules/_shared/components/feed-v2/utils/leadStatusAgentParser';
import { resolveStageLabel } from '@modules/_shared/components/feed-v2/utils/leadStatusAgentParser';
import type { Language } from '@shared/hooks/locale';

type Props = {
  stage: StageSnapshot | null | undefined;
  language: Language;
  /**
   * `from` renders a muted grey origin pill; `to` renders the emphasised
   * destination coloured by stage status: won = green, lost = red, active = blue.
   */
  variant?: 'from' | 'to';
};

export const StageChip = ({ stage, language, variant = 'to' }: Props) => {
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const label = resolveStageLabel(stage, language) ?? '—';

  const isFrom = variant === 'from';
  const statusColor =
    stage?.status === 'won'
      ? palette.success.main
      : stage?.status === 'lost'
      ? palette.error.main
      : palette.primary.main;
  const color = isFrom ? palette.text.secondary : statusColor;
  const bgcolor = isFrom
    ? palette.grey[100]
    : `color-mix(in srgb, ${color} 10%, transparent)`;
  const dotColor = isFrom ? palette.grey[400] : color;

  return (
    <Stack
      component="span"
      direction="row"
      spacing={0.5}
      sx={{
        ...baseChipSx,
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        bgcolor,
      }}
    >
      <Circle size={8} color={dotColor} fill={dotColor} />
      <Typography variant="caption" sx={{ color, ...chipTypographyStyles }}>
        {label}
      </Typography>
    </Stack>
  );
};
